export const RESTAURANT_COMMERCIAL_DEMO_ID = "restaurant-commercial-demo-v1";
export const RESTAURANT_COMMERCIAL_DEMO_PUBLIC_PATH = "/demo/restaurant-commercial";

export type RestaurantCommercialCustomerSurface = "home" | "menu" | "order" | "reservation";
export type RestaurantCommercialAdminSection = "carta" | "mesas" | "reservas" | "pedidos";

export const RESTAURANT_COMMERCIAL_CUSTOMER_SURFACES: readonly RestaurantCommercialCustomerSurface[] = Object.freeze([
  "home",
  "menu",
  "order",
  "reservation",
]);

export const RESTAURANT_COMMERCIAL_ADMIN_SECTIONS: readonly RestaurantCommercialAdminSection[] = Object.freeze([
  "carta",
  "mesas",
  "reservas",
  "pedidos",
]);

const CUSTOMER_SEGMENTS: Readonly<Record<Exclude<RestaurantCommercialCustomerSurface, "home">, string>> = Object.freeze({
  menu: "carta",
  order: "pedir",
  reservation: "reservar",
});

const NAMED_BUSINESSES: Readonly<Record<string, string>> = Object.freeze({
  "casa-nativa": "Casa Nativa",
  "kiwi-gastrobar": "Kiwi Gastrobar",
});

function titleFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function restaurantCommercialBusinessName(slug: string): string {
  return NAMED_BUSINESSES[slug] ?? (titleFromSlug(slug) || "Restaurant Demo");
}

export function restaurantCommercialStorageSlug(slug: string): string {
  return `${slug}--${RESTAURANT_COMMERCIAL_DEMO_ID}`;
}

export function restaurantCommercialCustomerPath(
  slug: string,
  surface: RestaurantCommercialCustomerSurface = "home",
): string {
  const base = `${RESTAURANT_COMMERCIAL_DEMO_PUBLIC_PATH}/${encodeURIComponent(slug)}`;
  const segment = surface === "home" ? "" : `/${CUSTOMER_SEGMENTS[surface]}`;
  return `${base}${segment}`;
}

export function restaurantCommercialAdminPath(
  slug: string,
  section: RestaurantCommercialAdminSection = "pedidos",
): string {
  return `${RESTAURANT_COMMERCIAL_DEMO_PUBLIC_PATH}/${encodeURIComponent(slug)}?view=admin&section=${encodeURIComponent(section)}`;
}

export function parseRestaurantCommercialPath(pathname: string): {
  readonly businessSlug: string | null;
  readonly surface: RestaurantCommercialCustomerSurface;
} {
  const suffix = pathname.startsWith(RESTAURANT_COMMERCIAL_DEMO_PUBLIC_PATH)
    ? pathname.slice(RESTAURANT_COMMERCIAL_DEMO_PUBLIC_PATH.length).replace(/^\/+|\/+$/g, "")
    : "";
  const [rawSlug, rawSurface] = suffix.split("/");
  let businessSlug: string | null = null;
  if (rawSlug) {
    try { businessSlug = decodeURIComponent(rawSlug); }
    catch { businessSlug = rawSlug; }
  }
  const surface: RestaurantCommercialCustomerSurface = rawSurface === "carta"
    ? "menu"
    : rawSurface === "pedir"
      ? "order"
      : rawSurface === "reservar"
        ? "reservation"
        : "home";
  return Object.freeze({ businessSlug, surface });
}
